import type { FC, ReactNode } from 'react'
import { Box, Typography } from '@mui/material'

interface EmptyStateProps {
  /** Icon rendered in a neutral-tone tile; decorative. */
  icon?: ReactNode
  title: string
  caption?: ReactNode
  /** Optional call-to-action rendered below the caption. */
  action?: ReactNode
}

/**
 * Centered empty/placeholder state (empty lists, coming-soon tabs): icon tile,
 * title, caption, optional action. Colors come from the active theme's tones
 * and text palette.
 */
export const EmptyState: FC<EmptyStateProps> = ({ icon, title, caption, action }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      gap: 1,
      px: 3,
      py: 6,
    }}
  >
    {icon && (
      <Box
        aria-hidden
        sx={theme => ({
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 42,
          height: 42,
          borderRadius: 1,
          bgcolor: theme.palette.tones.neutral.bg,
          color: theme.palette.tones.neutral.fg,
          mb: 0.5,
          '& svg': { fontSize: '2.2rem' },
        })}
      >
        {icon}
      </Box>
    )}
    <Typography sx={{ fontSize: '1.5rem', fontWeight: 600, color: 'text.primary' }}>
      {title}
    </Typography>
    {caption && (
      <Typography sx={{ fontSize: '1.3rem', color: 'text.secondary', maxWidth: 420 }}>
        {caption}
      </Typography>
    )}
    {action && <Box sx={{ mt: 1 }}>{action}</Box>}
  </Box>
)
